import type { Store, Dispatch } from 'redux';

import constants, { publicConstants } from '../../../constants';
import PubSubUtils from '../../../utils/pubsub-utils';
import Utils from '../../../utils/utils';
import { REMOVE_CONTAINER_ITEM, REPLACE_CONTAINER_ITEM } from '../actions';
import GarbageCollector from '../../../garbage-collector/index';

const { PUB_SUB_EVENTS } = publicConstants;

const handleRemoveContainerItem = (
  store: Store,
  target: string | undefined,
  containerItemID: string | undefined,
  eventName: string | undefined,
  newAccessedOrder: string[] | undefined,
  containerType: string | undefined
) => {
  const { resourceType, key, context } =
    Utils.getIn(store.getState(), ['containers', target, 'items', containerItemID]) || {};
  const eventPayload = {
    resourceType,
    key,
    context,
    containerItemID,
    ...(eventName !== undefined ? { eventName } : {})
  };
  PubSubUtils.publish(PUB_SUB_EVENTS.CONTAINER_EVENTS.CLOSE_CONTAINER_ITEM, eventPayload);
  if (
    target === `${publicConstants.APP.APP}/${constants.PRIMARY}` &&
    containerType === constants.CONTAINER_TYPE.MULTIPLE
  ) {
    PubSubUtils.publish(PUB_SUB_EVENTS.CONTAINER_EVENTS.CLOSE_MULTI_TYPE_PRIMARY_CONTAINER_ITEM, {
      key,
      newAccessedOrder
    });
  }
};

const cleanChildCaches = (type: string, childContainersContexts: (string | null)[]) => {
  if (
    childContainersContexts &&
    childContainersContexts.length > 0 &&
    (type === REPLACE_CONTAINER_ITEM || type === REMOVE_CONTAINER_ITEM)
  ) {
    childContainersContexts.forEach((childContext) => {
      if (childContext) {
        GarbageCollector.cleanCaches(childContext);
      }
    });
  }
};

/**
 * Redux middleware to track the redux changes
 * and publish pub-sub events
 * @param store the store that will be affected
 */

const PubSubMiddleware =
  (store: Store) =>
  (next: Dispatch) =>
  (action: {
    type: string;
    payload?: {
      target: string;
      reference?: string;
      containerItemID: string;
      context: string;
      childContainersContexts?: (string | null)[];
      containerInfo?: { newAccessedOrder: string[]; containerType: string };
      actionPerformed?: string;
      eventName?: string;
    };
  }) => {
    const {
      type,
      payload: {
        target,
        containerItemID,
        context: dataContext,
        childContainersContexts = [],
        containerInfo: { newAccessedOrder, containerType } = {
          newAccessedOrder: new Array<string>(),
          containerType: ''
        },
        eventName
      } = {}
    } = action;

    if (type === REMOVE_CONTAINER_ITEM) {
      handleRemoveContainerItem(store, target, containerItemID, eventName, newAccessedOrder, containerType);
    }

    cleanChildCaches(type, childContainersContexts);

    if ((type === REPLACE_CONTAINER_ITEM || type === REMOVE_CONTAINER_ITEM) && dataContext) {
      GarbageCollector.cleanCaches(dataContext);
    }
    next(action);
  };

export default PubSubMiddleware;
